import type { Middleware } from "@reduxjs/toolkit";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";

// ──────────────────────────────────────────────────────────────────────────────────────────────
// TEMP diagnostic. Logs every Redux write that feeds the lighting converger (and the reconnect /
// initial-state path) so a single capture from a misbehaving tablet shows WHAT state is flapping
// and HOW FAST — independent of where it originates (idle presence, subscription frame, re-query
// loop, etc.). Each line carries the ms-delta since the previous watched write and a per-type
// counter, so rapid churn is obvious (small deltas, fast-climbing counters).
//
// Grep tag: "[state-debug]". Remove this file and its store entry once the cause is found.
// ──────────────────────────────────────────────────────────────────────────────────────────────

const WATCHED = new Set<string>([
	"appState/setAppState", // idle-presence → ACTIVE/IDLE (the P1 driver)
	"appState/resetAppState",
	"appState/setInitialDataLoaded", // flips true on every initial-state-query completion → re-query loop
	"lighting/setLightingMode", // actual fridge lighting echo (subscription + initial query)
	"transaction/setOrder",
	"transaction/setRefill",
	"transaction/finishOrderState",
	"dishes/setInitialDishTypes", // re-dispatched by the initial-state query
	"dishes/setDishTypes", // per-dish lighting echo (spotlight) from the subscription — shows if the BE applies a TOGGLE
	"door/setDoorState",
	"door/setDoorLockState",
	"door/setLockState",
	"fridgeInfo/setFridgeState",
]);

const counters: Record<string, number> = {};
let lastTimestamp = 0;

const summarize = (payload: unknown): string => {
	if (payload == null || typeof payload !== "object") {
		return String(payload);
	}
	const record = payload as Record<string, unknown>;
	if (Array.isArray(record)) {
		return `len=${record.length}`;
	}
	if ("dishTypes" in record && Array.isArray(record.dishTypes)) {
		// Count how many echoed dishes carry a lighting mode, and how many are SPOTLIGHT, so the trace
		// shows whether the BE actually echoed a per-dish spotlight back (echo>0) and which it lit.
		const dishTypes = record.dishTypes as Array<{ lighting?: { mode?: string } }>;
		const withEcho = dishTypes.filter((dishType) => dishType?.lighting?.mode != null).length;
		const spot = dishTypes.filter((dishType) => dishType?.lighting?.mode === "SPOTLIGHT").length;
		return `dishTypes=${dishTypes.length} echo=${withEcho} spot=${spot}`;
	}
	if ("state" in record) {
		return `state=${String(record.state)} id=${String(record.id ?? "?")}`;
	}
	if ("lock" in record || "door" in record) {
		return JSON.stringify(record);
	}
	return "{…}";
};

export const stateDebugMiddleware: Middleware = () => (next) => (action) => {
	const type =
		typeof action === "object" && action !== null && "type" in action ? String((action as { type: unknown }).type) : "";

	// Master tablet + diagnostic flag only (see shouldTraceLightingDebug). Keeps the diagnostic off
	// for customers and out of integration tests / standard tablets. The check is read only for
	// watched actions, so the cost is negligible.
	if (WATCHED.has(type) && shouldTraceLightingDebug()) {
		const now = Date.now();
		const delta = lastTimestamp === 0 ? 0 : now - lastTimestamp;
		lastTimestamp = now;
		counters[type] = (counters[type] ?? 0) + 1;
		const payload = (action as { payload?: unknown }).payload;
		debugTrace(`[state-debug] +${delta}ms #${counters[type]} ${type} ${summarize(payload)}`);
	}

	return next(action);
};
